import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import type { Insight } from '@claude-code-insights/shared';
import { fetchInsight, updateInsight, fetchDeepDive, fetchRelatedInsights, type DeepDiveResult, type RelatedInsightCard } from '../lib/api';

function isGithubRepoUrl(url: string): boolean {
  try {
    const u = new URL(url);
    return u.hostname === 'github.com' && u.pathname.split('/').filter(Boolean).length === 2;
  } catch {
    return false;
  }
}

export function InsightView() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [insight, setInsight] = useState<Insight | null>(null);
  const [markdown, setMarkdown] = useState('');
  const [loading, setLoading] = useState(true);
  const [deepDive, setDeepDive] = useState<DeepDiveResult | null>(null);
  const [deepDiveLoading, setDeepDiveLoading] = useState(false);
  const [deepDiveError, setDeepDiveError] = useState<string | null>(null);
  const [related, setRelated] = useState<RelatedInsightCard[]>([]);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setRelated([]);
    fetchInsight(id)
      .then((result) => {
        setInsight(result.insight);
        setMarkdown(result.markdownContent);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
    fetchRelatedInsights(id).then(setRelated);
  }, [id]);

  async function handleToggleBookmark() {
    if (!insight || !id) return;
    try {
      const updated = await updateInsight(id, { bookmarked: !insight.bookmarked });
      setInsight((prev) => (prev ? { ...prev, ...updated } : prev));
    } catch (err) {
      console.error('Failed to toggle bookmark:', err);
    }
  }

  async function handleRating(rating: number) {
    if (!insight || !id) return;
    try {
      // Click same rating to clear it
      const newRating = insight.userRating === rating ? null : rating;
      const updated = await updateInsight(id, { userRating: newRating });
      setInsight((prev) => (prev ? { ...prev, ...updated } : prev));
    } catch (err) {
      console.error('Failed to set rating:', err);
    }
  }

  async function handleDeepDive() {
    if (!id || deepDiveLoading) return;
    if (deepDive) { setDeepDive(null); return; } // toggle off
    setDeepDiveLoading(true);
    setDeepDiveError(null);
    try {
      const result = await fetchDeepDive(id);
      setDeepDive(result);
    } catch (err) {
      setDeepDiveError(err instanceof Error ? err.message : 'Research failed');
    } finally {
      setDeepDiveLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="p-6 text-center text-obsidian-text-muted">Loading...</div>
    );
  }

  if (!insight) {
    return (
      <div className="p-6 text-center">
        <p className="text-obsidian-text-muted">Insight not found</p>
        <Link to="/" className="text-obsidian-accent text-sm mt-2 inline-block">
          Back to feed
        </Link>
      </div>
    );
  }

  // Strip frontmatter and AI-generated sections from markdown, keep only original content
  const originalContent = markdown
    .replace(/^---[\s\S]*?---\s*/m, '') // strip YAML frontmatter
    .replace(/## Summary[\s\S]*?(?=## |$)/, '') // strip Summary section
    .replace(/## Actionable Steps[\s\S]*?(?=## |$)/, '') // strip Actionable Steps section
    .replace(/## Related Topics[\s\S]*?(?=## |$)/, '') // strip Related Topics section
    .replace(/## Original Content\s*\n?/, '') // strip the heading itself
    .trim();

  return (
    <div className="flex flex-col md:flex-row h-full">
      {/* Main content */}
      <div className="flex-1 overflow-auto p-3 md:p-6">
        <Link
          to="/"
          className="text-sm text-obsidian-text-muted hover:text-obsidian-text mb-4 inline-block"
        >
          &larr; Back to feed
        </Link>

        <h1 className="text-xl font-semibold mt-2 mb-6">{insight.title}</h1>

        {/* Summary */}
        <div className="border-l-2 border-obsidian-accent pl-4 py-2 mb-6 bg-obsidian-accent/5 rounded-r">
          <p className="text-sm text-obsidian-text leading-relaxed">{insight.summary}</p>
        </div>

        {/* Actionable Steps */}
        {insight.actionableSteps.length > 0 && (
          <div className="mb-6">
            <h2 className="text-sm font-semibold text-obsidian-text-muted uppercase tracking-wide mb-3">
              Actionable Steps
            </h2>
            <ol className="space-y-2">
              {insight.actionableSteps.map((step, i) => (
                <li key={i} className="flex gap-3 text-sm">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-obsidian-accent/20 text-obsidian-accent flex items-center justify-center text-xs font-medium">
                    {i + 1}
                  </span>
                  <span className="text-obsidian-text leading-relaxed pt-0.5">{step}</span>
                </li>
              ))}
            </ol>
          </div>
        )}

        {/* Original Content (collapsible) */}
        {originalContent && (
          <details className="mb-6 group">
            <summary className="text-sm font-semibold text-obsidian-text-muted uppercase tracking-wide cursor-pointer hover:text-obsidian-text select-none mb-3">
              Original Content
            </summary>
            <div className="prose prose-invert prose-sm max-w-none border-t border-obsidian-border pt-4 text-obsidian-text-muted">
              <ReactMarkdown rehypePlugins={[rehypeRaw]}>
                {originalContent}
              </ReactMarkdown>
            </div>
          </details>
        )}

        {/* Deep Dive Results */}
        {deepDive && (
          <div className="mb-6 border border-obsidian-border rounded-lg overflow-hidden">
            <div className="flex items-center justify-between px-4 py-2.5 bg-obsidian-bg-secondary border-b border-obsidian-border">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-obsidian-text">⚡ Deep Dive Analysis</span>
                <span className="text-xs px-1.5 py-0.5 rounded" style={
                  deepDive.source === 'perplexity'
                    ? { background: 'rgba(99,102,241,0.15)', color: '#818cf8', border: '1px solid rgba(99,102,241,0.3)' }
                    : { background: 'rgba(255,255,255,0.06)', color: '#6b7280', border: '1px solid rgba(255,255,255,0.1)' }
                }>
                  {deepDive.source === 'perplexity' ? 'Perplexity' : 'HN + DDG + Claude'}
                </span>
              </div>
              <button
                onClick={() => setDeepDive(null)}
                className="text-xs text-obsidian-text-faint hover:text-obsidian-text transition-colors"
              >
                Clear ✕
              </button>
            </div>
            <div className="p-4 prose prose-invert prose-sm max-w-none text-obsidian-text-muted">
              <ReactMarkdown>{deepDive.analysis}</ReactMarkdown>
            </div>
            {deepDive.citations.length > 0 && (
              <div className="px-4 py-3 border-t border-obsidian-border">
                <p className="text-xs font-medium text-obsidian-text-faint uppercase tracking-wide mb-2">Sources</p>
                <div className="space-y-1">
                  {deepDive.citations.map((url, i) => {
                    let display = url;
                    try { display = new URL(url).hostname; } catch { /* keep full url */ }
                    return (
                      <a
                        key={i}
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1.5 text-xs text-obsidian-link hover:underline truncate"
                      >
                        <span className="flex-shrink-0 opacity-50">◎</span>
                        <span className="truncate">{display}</span>
                      </a>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Sidebar */}
      <aside className="w-full md:w-64 md:flex-shrink-0 border-t md:border-t-0 md:border-l border-obsidian-border p-3 md:p-4 overflow-auto">
        {/* Bookmark */}
        <div className="mb-4">
          <button
            onClick={handleToggleBookmark}
            className={`flex items-center gap-2 text-sm transition-colors ${
              insight.bookmarked
                ? 'text-obsidian-warning'
                : 'text-obsidian-text-faint hover:text-obsidian-warning'
            }`}
          >
            <span className="text-lg">{insight.bookmarked ? '\u2605' : '\u2606'}</span>
            {insight.bookmarked ? 'Bookmarked' : 'Bookmark'}
          </button>
        </div>

        {/* Your Rating */}
        <div className="mb-4">
          <h4 className="text-xs font-medium text-obsidian-text-faint uppercase tracking-wide mb-2">
            Your Rating
          </h4>
          <div className="flex gap-1">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                onClick={() => handleRating(star)}
                className={`text-lg transition-colors ${
                  (insight.userRating ?? 0) >= star
                    ? 'text-obsidian-warning'
                    : 'text-obsidian-text-faint hover:text-obsidian-warning'
                }`}
              >
                {(insight.userRating ?? 0) >= star ? '\u2605' : '\u2606'}
              </button>
            ))}
          </div>
        </div>

        {/* Deep Dive */}
        <div className="mb-4">
          <button
            onClick={handleDeepDive}
            disabled={deepDiveLoading}
            className={`flex items-center gap-2 text-sm transition-colors ${
              deepDive
                ? 'text-obsidian-accent'
                : 'text-obsidian-text-faint hover:text-obsidian-accent'
            } disabled:opacity-50`}
          >
            <span>{deepDiveLoading ? '⏳' : '⚡'}</span>
            {deepDiveLoading ? 'Researching...' : deepDive ? 'Clear Deep Dive' : 'Deep Dive'}
          </button>
          {deepDiveError && (
            <p className="text-xs text-red-400 mt-1">{deepDiveError}</p>
          )}
        </div>

        {/* Relevance */}
        <div className="mb-4">
          <h4 className="text-xs font-medium text-obsidian-text-faint uppercase tracking-wide mb-1">
            Relevance
          </h4>
          <span
            className={`text-2xl font-bold ${
              insight.relevanceScore >= 8
                ? 'text-obsidian-success'
                : insight.relevanceScore >= 5
                  ? 'text-obsidian-accent'
                  : 'text-obsidian-warning'
            }`}
          >
            {insight.relevanceScore}/10
          </span>
        </div>

        {/* Category */}
        <div className="mb-4">
          <h4 className="text-xs font-medium text-obsidian-text-faint uppercase tracking-wide mb-1">
            Category
          </h4>
          <span className="badge bg-obsidian-accent/20 text-obsidian-accent">
            {insight.category}
          </span>
        </div>

        {/* Source */}
        <div className="mb-4">
          <h4 className="text-xs font-medium text-obsidian-text-faint uppercase tracking-wide mb-1">
            Source
          </h4>
          <p className="text-sm text-obsidian-text-muted">{insight.sourceType}</p>
          <p className="text-sm text-obsidian-text-muted">{insight.sourceAuthor}</p>
          {insight.sourceUrl && (
            isGithubRepoUrl(insight.sourceUrl) ? (
              <button
                onClick={() => navigate(`/git?repo=${encodeURIComponent(insight.sourceUrl)}`)}
                className="text-sm text-obsidian-link hover:underline mt-1 inline-block text-left"
              >
                View in Git viewer ⎇
              </button>
            ) : (
              <a
                href={insight.sourceUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-obsidian-link hover:underline mt-1 inline-block"
              >
                Open original &rarr;
              </a>
            )
          )}
        </div>

        {/* Tags */}
        {insight.tags.length > 0 && (
          <div className="mb-4">
            <h4 className="text-xs font-medium text-obsidian-text-faint uppercase tracking-wide mb-2">
              Tags
            </h4>
            <div className="flex gap-1 flex-wrap">
              {insight.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-2 py-0.5 text-xs bg-obsidian-bg-tertiary text-obsidian-text-muted rounded cursor-pointer hover:bg-obsidian-surface"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Related Insights */}
        {related.length > 0 && (
          <div className="mb-4">
            <h4 className="text-xs font-medium text-obsidian-text-faint uppercase tracking-wide mb-2">
              Related Insights
            </h4>
            <div className="space-y-2">
              {related.map((r) => (
                <Link
                  key={r.insightId}
                  to={`/insight/${r.insightId}`}
                  className="block text-xs text-obsidian-link hover:underline leading-tight"
                >
                  <span className="text-obsidian-text-muted">{r.title}</span>
                  <span className="ml-1 text-obsidian-text-faint">· {r.relevanceScore}/10</span>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Enrichment Sources */}
        {insight.enrichmentSources && insight.enrichmentSources.length > 0 && (
          <div className="mb-4">
            <h4 className="text-xs font-medium text-obsidian-text-faint uppercase tracking-wide mb-2">
              Enrichment Sources
            </h4>
            <div className="space-y-1.5">
              {insight.enrichmentSources.map((srcUrl, i) => {
                const displayName = srcUrl.includes('github.com')
                  ? srcUrl.replace('https://github.com/', '')
                  : (() => { try { return new URL(srcUrl).hostname; } catch { return srcUrl; } })();
                const isRepoLink = isGithubRepoUrl(srcUrl);
                return isRepoLink ? (
                  <button
                    key={i}
                    onClick={() => navigate(`/git?repo=${encodeURIComponent(srcUrl)}`)}
                    className="flex items-center gap-1.5 text-xs text-obsidian-link hover:underline text-left"
                  >
                    <span className="flex-shrink-0 opacity-50">⎇</span>
                    <span className="truncate">{displayName}</span>
                  </button>
                ) : (
                  <a
                    key={i}
                    href={srcUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 text-xs text-obsidian-link hover:underline"
                  >
                    <span className="flex-shrink-0 opacity-50">
                      {srcUrl.includes('github.com') ? '\u2325' : '\u25CE'}
                    </span>
                    <span className="truncate">{displayName}</span>
                  </a>
                );
              })}
            </div>
            <p className="text-[10px] text-obsidian-text-faint mt-1.5">
              Auto-fetched to supplement short-form content
            </p>
          </div>
        )}

        {/* Date */}
        <div className="mb-4">
          <h4 className="text-xs font-medium text-obsidian-text-faint uppercase tracking-wide mb-1">
            Added
          </h4>
          <p className="text-sm text-obsidian-text-muted">
            {new Date(insight.createdAt).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })}
          </p>
        </div>
      </aside>
    </div>
  );
}
